import { z } from 'zod';
import {
  STOREFRONT_ICON_KEYS,
  type StorefrontConfig,
  type StorefrontIconKey,
} from '../storefront-content';

/**
 * Zod mirror of `StorefrontConfig` (see `../storefront-content.ts`). Used by the
 * `tenant_config.features` PATCH so the admin Storefront editor persists (a plain
 * z.object strips unknown keys — every editable sub-block must be listed). The
 * admin sends the FULL block on save, so this schema is non-partial except the
 * top-level key itself being `.optional()` in the features patch.
 */

const iconKeySchema = z.enum([...STOREFRONT_ICON_KEYS] as [
  StorefrontIconKey,
  ...StorefrontIconKey[],
]);
const objectKey = z.string().max(512);
const shortText = z.string().max(300);
const href = z.string().max(2048);

const heroSlideSchema = z.object({
  kicker: shortText,
  title: shortText,
  highlight: shortText,
  date: shortText,
  imageKey: objectKey.optional(),
});

const promoBannerSchema = z.object({
  title: shortText,
  sub: shortText,
  gradient: z.string().max(300).optional(),
  imageKey: objectKey.optional(),
});

const campaignBannerSchema = z.object({
  title: shortText,
  sub: shortText,
  ctaLabel: shortText,
  ctaHref: href,
  gradient: z.string().max(300).optional(),
  imageKey: objectKey.optional(),
});

const collectionSchema = z.object({
  name: shortText,
  categoryId: z.string().max(64).optional(),
  items: shortText.optional(),
  imageKey: objectKey.optional(),
});

const voucherSchema = z.object({
  big: shortText,
  sm: shortText,
  terms: shortText,
  validity: shortText,
});

const iconBadgeSchema = z.object({
  label: shortText,
  icon: iconKeySchema,
});

const productVoucherBadgeSchema = z.object({
  line1: shortText,
  line2: shortText,
});

const statSchema = z.object({
  value: shortText,
  label: shortText,
  live: z.literal('productCount').optional(),
});

const iconStatSchema = statSchema.extend({ icon: iconKeySchema });

const linkSchema = z.object({ label: shortText, href });

const footerColumnSchema = z.object({
  heading: shortText,
  links: z.array(linkSchema).max(20),
});

const longText = z.string().max(5000);

const pageSectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('paragraph'), heading: shortText.optional(), body: longText }),
  z.object({
    kind: z.literal('steps'),
    heading: shortText.optional(),
    steps: z.array(longText).max(50),
  }),
  z.object({
    kind: z.literal('docs'),
    heading: shortText.optional(),
    items: z.array(longText).max(50),
  }),
]);

const pageSchema = z.object({
  title: shortText,
  intro: longText.optional(),
  heroImageKey: objectKey.optional(),
  sections: z.array(pageSectionSchema).max(30),
  ctaLabel: shortText.optional(),
  ctaHref: href.optional(),
});

const faqEntrySchema = z.object({
  question: shortText,
  answer: longText,
  category: shortText.optional(),
});

const socialLinkSchema = z.object({
  platform: shortText,
  url: href,
  logoKey: objectKey.optional(),
});

const companyTestimonialSchema = z.object({
  author: shortText,
  role: shortText.optional(),
  quote: longText,
  avatarKey: objectKey.optional(),
});

const membershipTierSchema = z.object({
  name: shortText,
  price: shortText,
  pv: shortText.optional(),
  oneTimeLabel: shortText.optional(),
  features: z.array(shortText).max(12),
  ctaLabel: shortText,
  ctaHref: href,
  featured: z.boolean().optional(),
});

// --- Layout (Layer 2 section/page registry) — mirrors `storefront-sections.ts`.
// Bounded everywhere (closed enums, clamped numbers, .max() arrays) so it stays
// a layout editor, never a CMS.
const sectionStyleSchema = z.object({
  bg: z.enum(['page', 'card', 'tint', 'transparent']).optional(),
  spacing: z.enum(['tight', 'normal', 'loose']).optional(),
});
const sectionId = z.string().max(64);

const sectionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hero'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      sideBanners: z.boolean().optional(),
      autoAdvanceMs: z.number().int().min(0).max(60000).optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('flashSale'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({ title: shortText.optional(), seeAllHref: href.optional() }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('featuredPackages'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      heading: shortText.optional(),
      subheading: shortText.optional(),
      cols: z.union([z.literal(3), z.literal(4), z.literal(5)]).optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('categoryGrid'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      cols: z.union([z.literal(4), z.literal(8), z.literal(10)]).optional(),
      limit: z.number().int().min(1).max(50).optional(),
      linkBase: href.optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('productFeed'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      title: shortText.optional(),
      cols: z.union([z.literal(3), z.literal(4), z.literal(6)]).optional(),
      pageSize: z.number().int().min(1).max(60).optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('productRow'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      title: shortText,
      source: z.enum(['bestsellers', 'newArrivals', 'topSelling']),
      showPv: z.boolean().optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('bannerGroup'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      source: z.enum(['bottomBanners', 'homeSideBanners', 'campaignBanners']),
      cols: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('richLanding'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      variant: z.enum(['corporateHero', 'welcome', 'ctaBanner']),
      eyebrow: shortText.optional(),
      heading: shortText,
      subheading: longText.optional(),
      bodyParas: z.array(longText).max(4).optional(),
      backgroundImageKey: objectKey.optional(),
      overlay: z.boolean().optional(),
      align: z.enum(['center', 'left']).optional(),
      primaryCta: z.object({ label: shortText, href }).optional(),
      secondaryCta: z.object({ label: shortText, href }).optional(),
      featureCards: z
        .array(z.object({ icon: iconKeySchema, title: shortText, body: longText }))
        .max(4)
        .optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('valuesGrid'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      heading: shortText,
      subheading: longText.optional(),
      cols: z.union([z.literal(3), z.literal(4)]).optional(),
      items: z.array(z.object({ icon: iconKeySchema, title: shortText, body: longText })).max(8),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('howItWorks'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      heading: shortText,
      subheading: longText.optional(),
      steps: z.array(z.object({ title: shortText, body: longText })).max(6),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('packages'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      heading: shortText,
      subheading: longText.optional(),
      source: z.literal('membershipTiers').optional(),
      cols: z.union([z.literal(3), z.literal(4)]).optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('testimonials'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      heading: shortText,
      subheading: longText.optional(),
      cols: z.union([z.literal(2), z.literal(3)]).optional(),
      source: z.literal('company').optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('rankShowcase'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      heading: shortText.optional(),
      subheading: longText.optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('cmsArticle'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      pageKey: z.enum(['about', 'becomeMember', 'sellOnOmni', 'merchantGuide']),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('spacer'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({ height: z.enum(['sm', 'md', 'lg']) }),
    style: sectionStyleSchema.optional(),
  }),
  // Learning storefront
  z.object({
    type: z.literal('courseHero'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      title: shortText,
      subheading: longText.optional(),
      ctaLabel: shortText.optional(),
      ctaHref: href.optional(),
      imageKey: objectKey.optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('valueProps'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      items: z.array(z.object({ icon: iconKeySchema, title: shortText, body: longText })).max(6),
      earnItem: z.object({ icon: iconKeySchema, title: shortText, body: longText }).optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('courseCategoryNav'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({ limit: z.number().int().min(1).max(20).optional() }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('courseGrid'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      title: shortText.optional(),
      viewAllHref: href.optional(),
      limit: z.union([z.literal(4), z.literal(8), z.literal(12)]).optional(),
      cols: z.union([z.literal(3), z.literal(4)]).optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
  z.object({
    type: z.literal('resellCta'),
    id: sectionId,
    enabled: z.boolean(),
    config: z.object({
      title: shortText,
      body: longText.optional(),
      ctaLabel: shortText.optional(),
      ctaHref: href.optional(),
    }),
    style: sectionStyleSchema.optional(),
  }),
]);

const layoutPageSchema = z.object({
  path: z.string().max(256),
  title: shortText.optional(),
  header: z.boolean().optional(),
  footer: z.boolean().optional(),
  scrollNav: z.boolean().optional(),
  headerVariant: z.enum(['market', 'learning']).optional(),
  footerVariant: z.enum(['light', 'dark']).optional(),
  background: z.enum(['muted', 'white']).optional(),
  sections: z.array(sectionSchema).max(40),
});

const layoutSchema = z.object({
  version: z.number().int(),
  preset: z.string().max(64).optional(),
  pages: z.array(layoutPageSchema).max(30),
  homePath: z.string().max(256).optional(),
});

/**
 * Guard for the bespoke-site "Site Pages" overlay. Deliberately generic (not
 * derived from the per-tenant manifest): writers already hold
 * `tenant.storefront.write`, unknown paths are inert on the member side, and a
 * tenant-agnostic schema keeps the PATCH route free of TENANT awareness. Bounds
 * keep the blob small; depth is structurally capped by the flat-map shape.
 */
const sitePath = z
  .string()
  .min(3)
  .max(160)
  .regex(/^[a-z0-9_-]+(\.[a-z0-9_-]+){1,3}$/i, 'must be a dotted field path');
const siteListItem = z.record(z.string().max(64), z.string().max(2000));
export const siteContentOverlaySchema = z
  .object({
    text: z.record(sitePath, z.string().max(4000)).optional(),
    lists: z.record(sitePath, z.array(siteListItem).max(60)).optional(),
    images: z.record(sitePath, objectKey).optional(),
  })
  .superRefine((v, ctx) => {
    if (Object.keys(v.text ?? {}).length > 600)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['text'], message: 'too many entries' });
    if (Object.keys(v.lists ?? {}).length > 120)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lists'], message: 'too many entries' });
    if (Object.keys(v.images ?? {}).length > 200)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['images'], message: 'too many entries' });
  });

export const storefrontConfigSchema = z.object({
  heroSlides: z.array(heroSlideSchema).max(20),
  homeSideBanners: z.array(promoBannerSchema).max(20),
  bottomBanners: z.array(promoBannerSchema).max(20),
  campaignBanners: z.array(campaignBannerSchema).max(20),
  collections: z.array(collectionSchema).max(50),
  vouchers: z.array(voucherSchema).max(20),
  trustBadges: z.array(iconBadgeSchema).max(20),
  assuranceBadges: z.array(iconBadgeSchema).max(20),
  productVoucherBadges: z.array(productVoucherBadgeSchema).max(10),
  shopHeader: z.object({ stats: z.array(iconStatSchema).max(12) }),
  productStoreCard: z.object({ stats: z.array(statSchema).max(12) }),
  header: z.object({
    utilLinks: z.array(linkSchema).max(12),
    searchSuggestions: z.array(shortText).max(20),
  }),
  footer: z.object({
    brandName: shortText,
    brandBlurb: z.string().max(1000),
    copyrightText: shortText,
    permitText: shortText,
    columns: z.array(footerColumnSchema).max(8),
  }),
  brand: z.object({
    brandName: shortText,
    siteTitle: shortText,
    companyName: shortText,
    houseStoreName: shortText,
    marketplaceSellerName: shortText,
    mallBadgeText: shortText,
    showMallBadge: z.boolean(),
    walletLabel: shortText,
    kitLabel: shortText,
  }),
  copy: z.object({
    cartVoucherHint: shortText,
    cartHouseVoucherHint: shortText,
    shippingGuarantee: shortText,
  }),
  pages: z.object({
    about: pageSchema,
    becomeMember: pageSchema,
    sellOnOmni: pageSchema,
    merchantGuide: pageSchema,
  }),
  faq: z.object({
    title: shortText,
    intro: longText.optional(),
    entries: z.array(faqEntrySchema).max(100),
  }),
  social: z.array(socialLinkSchema).max(20),
  company: z.object({
    mission: longText,
    vision: longText,
    profile: longText,
    testimonials: z.array(companyTestimonialSchema).max(50),
  }),
  payment: z.object({
    title: shortText,
    intro: longText.optional(),
  }),
  learningHome: z.object({
    hero: z.object({
      title: shortText,
      subheading: longText,
      ctaLabel: shortText,
      imageKey: objectKey.or(z.literal('')),
    }),
    valueProps: z.array(z.object({ icon: iconKeySchema, title: shortText, body: longText })).max(6),
    earnProp: z.object({ icon: iconKeySchema, title: shortText, body: longText }),
    featuredTitle: shortText,
    resell: z.object({ title: shortText, body: longText, ctaLabel: shortText, ctaHref: href }),
    linkToStore: z.boolean(),
    linkFromStore: z.boolean(),
  }),
  showDecorativeSold: z.boolean(),
  showCourseRatings: z.boolean(),
  showResellBanner: z.boolean(),
  memberExclusiveDealProductIds: z.array(z.string().uuid()).max(50),
  membershipTiers: z.array(membershipTierSchema).max(12),
  layout: layoutSchema,
  siteContent: siteContentOverlaySchema.optional(),
});

/**
 * Compile-time guard: the Zod schema's inferred type must stay in lockstep with
 * `StorefrontConfig`. If either drifts, one of these assignments fails to compile.
 */
type _SchemaToType = z.infer<typeof storefrontConfigSchema> extends StorefrontConfig ? true : never;
type _TypeToSchema = StorefrontConfig extends z.infer<typeof storefrontConfigSchema> ? true : never;
const _schemaMatchesType: _SchemaToType = true;
const _typeMatchesSchema: _TypeToSchema = true;
void _schemaMatchesType;
void _typeMatchesSchema;
